import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

// Module global recherche — drawer présent sur toutes les pages
const barcinoSearch = {

    _listeners: [],
    _favSwiper: null,

    init() {
        this.initSearch();
        this.initQuickAdd();
        // Swiper initialisé en lazy (première ouverture) pour garantir les dimensions
    },

    // ─── Drawer recherche + live search ──────────────────────────────────────
    initSearch() {
        const drawer    = document.getElementById('search-drawer');
        if (!drawer) return;

        const triggers  = document.querySelectorAll('[data-action="search"]');
        const closeBtns = document.querySelectorAll('[data-search-close]');
        const input     = drawer.querySelector('[data-search-input]');
        const clearBtn  = drawer.querySelector('[data-search-clear]');
        const emptyEl   = drawer.querySelector('[data-search-empty]');
        const resultsEl = drawer.querySelector('[data-search-results]');
        const loaderEl  = drawer.querySelector('[data-search-loader]');
        const noResultsEl = drawer.querySelector('[data-no-results]');
        const tabs      = drawer.querySelectorAll('[data-tab]');

        const ajaxUrl   = drawer.dataset.searchAjaxUrl;
        const logUrl    = drawer.dataset.searchLogUrl;
        const labelFrom = drawer.dataset.labelFrom;
        const labelRead = drawer.dataset.labelReading;

        let debounceTimer = null;
        let commitTimer   = null;
        let activeTab     = 'products';

        // ── Journalisation « recherche populaire » ────────────────────────
        // On ne logue pas chaque frappe : uniquement une query stabilisée
        // qui a renvoyé des résultats (1,5 s sans nouvelle saisie) ou un
        // clic sur un résultat. Dédoublonnage par session de drawer.
        const committed  = new Set();
        let   lastQuery  = '';
        let   lastCount  = 0;

        const sendCommit = (q, count) => {
            q = (q || '').trim();
            if (!logUrl || q.length < 3 || count < 1) return;
            const key = q.toLowerCase();
            if (committed.has(key)) return;
            committed.add(key);
            try {
                navigator.sendBeacon(
                    logUrl,
                    new URLSearchParams({ q, results_count: String(count) })
                );
            } catch (e) { /* no-op */ }
        };

        const scheduleCommit = () => {
            clearTimeout(commitTimer);
            commitTimer = setTimeout(() => sendCommit(lastQuery, lastCount), 1500);
        };

        // ── Open / Close ──────────────────────────────────────────────────
        const openDrawer = () => {
            drawer.classList.add('is-open');
            drawer.setAttribute('aria-hidden', 'false');
            drawer.inert = false;
            document.body.style.overflow = 'hidden';
            // Après la transition CSS (.4s), focus + recalcul Swiper
            setTimeout(() => {
                input && input.focus();
                // Lazy init : premier open → init Swiper quand le panel est visible
                if (!this._favSwiper) {
                    this.initFavSwiper();
                } else {
                    this._favSwiper.update();
                }
            }, 420);
        };

        const closeDrawer = () => {
            drawer.classList.remove('is-open');
            drawer.setAttribute('aria-hidden', 'true');
            drawer.inert = true;
            document.body.style.overflow = '';
        };

        triggers.forEach(btn => btn.addEventListener('click', openDrawer));
        closeBtns.forEach(el => el.addEventListener('click', closeDrawer));

        const onKeydown = (e) => { if (e.key === 'Escape') closeDrawer(); };
        document.addEventListener('keydown', onKeydown);
        this._listeners.push({ el: document, type: 'keydown', fn: onKeydown });

        // ── Clear input ───────────────────────────────────────────────────
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                input.value = '';
                clearBtn.hidden = true;
                showEmpty();
                input.focus();
            });
        }

        // ── Helpers affichage ─────────────────────────────────────────────
        const showEmpty = () => {
            emptyEl && (emptyEl.hidden = false);
            resultsEl && (resultsEl.hidden = true);
            loaderEl && (loaderEl.hidden = true);
        };

        const showLoader = () => {
            emptyEl && (emptyEl.hidden = true);
            resultsEl && (resultsEl.hidden = true);
            loaderEl && (loaderEl.hidden = false);
        };

        const showResults = () => {
            emptyEl && (emptyEl.hidden = true);
            loaderEl && (loaderEl.hidden = true);
            resultsEl && (resultsEl.hidden = false);
        };

        // ── Rendu d'un item produit ───────────────────────────────────────
        const renderProduct = (p) => {
            const li = document.createElement('li');
            const tagsHtml = (p.tags || []).map(t =>
                `<span class="search-drawer__tag">${t}</span>`
            ).join('');
            const imgHtml = p.image_url
                ? `<img src="${p.image_url}" alt="${p.name}" loading="lazy">`
                : '';
            const descParts = [p.grind, p.profile, p.composition].filter(Boolean).join(' • ');
            const oldPriceHtml = p.old_price
                ? `<span class="search-drawer__result-price-old">${p.old_price} €</span>`
                : '';
            const discountHtml = p.discount_label
                ? `<span class="search-drawer__result-promo-badge">${p.discount_label}</span>`
                : '';

            li.innerHTML = `
                <a href="${p.url}" class="search-drawer__result-item prevent-barba">
                    <div class="search-drawer__result-img">${imgHtml}</div>
                    <div class="search-drawer__result-body">
                        <span class="search-drawer__result-name">${p.name}</span>
                        ${p.brand ? `<span class="search-drawer__result-brand">${p.brand}</span>` : ''}
                        ${descParts ? `<span class="search-drawer__result-desc">${descParts}</span>` : ''}
                        ${tagsHtml ? `<div class="search-drawer__result-tags">${tagsHtml}</div>` : ''}
                    </div>
                    <div class="search-drawer__result-price">
                        <span class="search-drawer__result-price-label">${labelFrom}</span>
                        ${oldPriceHtml}
                        <span class="search-drawer__result-price-value">${p.price} €</span>
                        ${discountHtml}
                    </div>
                </a>`;
            return li;
        };

        // ── Rendu d'un item article ───────────────────────────────────────
        const renderArticle = (a) => {
            const li = document.createElement('li');
            const imgHtml = a.image_url
                ? `<img src="${a.image_url}" alt="${a.title}" loading="lazy">`
                : '';
            const dateHtml = a.date_label ? `<span class="search-drawer__result-date">${a.date_label}</span>` : '';

            li.innerHTML = `
                <a href="${a.url}" class="search-drawer__result-item prevent-barba">
                    <div class="search-drawer__result-img search-drawer__result-img--photo">${imgHtml}</div>
                    <div class="search-drawer__result-body">
                        <span class="search-drawer__result-name">${a.title}</span>
                        ${a.excerpt ? `<span class="search-drawer__result-desc">${a.excerpt}</span>` : ''}
                        ${dateHtml}
                    </div>
                </a>`;
            return li;
        };

        // ── Render résultats ──────────────────────────────────────────────
        const renderResults = (data) => {
            const panelP = drawer.querySelector('[data-result-panel="products"]');
            const panelA = drawer.querySelector('[data-result-panel="articles"]');
            const countP = drawer.querySelector('[data-tab-count="products"]');
            const countA = drawer.querySelector('[data-tab-count="articles"]');

            panelP.innerHTML = '';
            panelA.innerHTML = '';

            const total = (data.products?.length || 0) + (data.articles?.length || 0);

            if (countP) countP.textContent = data.products?.length || '';
            if (countA) countA.textContent = data.articles?.length || '';

            // Un onglet sans résultat ne doit pas pouvoir être ouvert —
            // pas de panneau vide accessible au clic.
            tabs.forEach(btn => {
                const count = btn.dataset.tab === 'products' ? (data.products?.length || 0) : (data.articles?.length || 0);
                btn.disabled = count === 0;
                btn.classList.toggle('is-disabled', count === 0);
            });

            if (total === 0) {
                noResultsEl && (noResultsEl.hidden = false);
                showResults();
                return;
            }
            noResultsEl && (noResultsEl.hidden = true);

            (data.products || []).forEach(p => panelP.appendChild(renderProduct(p)));
            (data.articles || []).forEach(a => panelA.appendChild(renderArticle(a)));

            const startTab = data.products?.length > 0 ? 'products' : 'articles';
            switchTab(startTab);
            showResults();
        };

        // ── Tabs ──────────────────────────────────────────────────────────
        const switchTab = (tab) => {
            activeTab = tab;
            tabs.forEach(btn => {
                const isActive = btn.dataset.tab === tab;
                btn.classList.toggle('is-active', isActive);
                btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });
            drawer.querySelectorAll('[data-result-panel]').forEach(panel => {
                panel.hidden = panel.dataset.resultPanel !== tab;
            });
        };

        tabs.forEach(btn => btn.addEventListener('click', () => switchTab(btn.dataset.tab)));

        // ── Recherche (partagée par la saisie et les suggestions cliquables) ──
        const runSearch = async (q) => {
            showLoader();
            try {
                const res = await fetch(`${ajaxUrl}?q=${encodeURIComponent(q)}`, {
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                });
                if (!res.ok) throw new Error('HTTP ' + res.status);
                const data = await res.json();
                renderResults(data);
                lastQuery = q;
                lastCount = (data.products?.length || 0) + (data.articles?.length || 0);
                scheduleCommit();
            } catch {
                loaderEl && (loaderEl.hidden = true);
                showEmpty();
            }
        };

        // ── Live search (debounce 350ms, min 2 chars) ─────────────────────
        if (input) {
            input.addEventListener('input', () => {
                const q = input.value.trim();

                if (clearBtn) clearBtn.hidden = q.length === 0;

                clearTimeout(debounceTimer);
                clearTimeout(commitTimer);

                if (q.length < 2) {
                    showEmpty();
                    return;
                }

                debounceTimer = setTimeout(() => runSearch(q), 350);
            });
        }

        // ── Suggestions cliquables (recherches populaires) ─────────────────
        // Déclenchent la même recherche live que la saisie, sans navigation —
        // il n'existe pas de page /recherche à part entière sur le site.
        drawer.querySelectorAll('[data-search-populate]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const term = btn.dataset.searchPopulate || '';
                if (!input || term === '') return;

                input.value = term;
                if (clearBtn) clearBtn.hidden = false;
                clearTimeout(debounceTimer);
                // Ne pas re-logguer un terme déjà proposé (évite la boucle
                // « populaire car proposé car populaire »).
                committed.add(term.toLowerCase());
                runSearch(term);
            });
        });

        // ── Clic sur un résultat = recherche validée → log immédiat ────────
        if (resultsEl) {
            resultsEl.addEventListener('click', (e) => {
                if (!e.target.closest('.search-drawer__result-item')) return;
                clearTimeout(commitTimer);
                sendCommit(lastQuery, lastCount);
            });
        }
    },

    // ─── Modal Quick Add — choix du poids avant ajout panier ─────────────────
    initQuickAdd() {
        const modal = document.getElementById('quick-add-modal');
        if (!modal) return;

        const thumbEl    = modal.querySelector('[data-quick-thumb]');
        const nameEl     = modal.querySelector('[data-quick-name]');
        const roasterEl  = modal.querySelector('[data-quick-roaster]');
        const grindEl    = modal.querySelector('[data-quick-grind]');
        const sepEl      = modal.querySelector('[data-quick-sep]');
        const priceEl    = modal.querySelector('[data-quick-price]');
        const priceOldEl = modal.querySelector('[data-quick-price-old]');
        const discountEl = modal.querySelector('[data-quick-discount]');
        const kiloEl     = modal.querySelector('[data-quick-price-kilo]');
        const chipsEl    = modal.querySelector('[data-quick-chips]');
        const selectorEl = chipsEl ? chipsEl.closest('.quick-add-modal__selector') : null;
        const labelEl    = modal.querySelector('[data-quick-selector-label]');
        const qtyValEl   = modal.querySelector('[data-quick-qty-val]');
        const totalEl    = modal.querySelector('[data-quick-total]');
        const submitBtn  = modal.querySelector('[data-quick-submit]');
        const decBtn     = modal.querySelector('[data-quick-dec]');
        const incBtn     = modal.querySelector('[data-quick-inc]');

        let selectedVariantId = null;
        let selectedPrice     = 0;
        let qty               = 1;

        const formatPrice = (val) => document.documentElement.lang === 'en'
            ? val.toFixed(2) + ' €'
            : val.toFixed(2).replace('.', ',') + ' €';

        const updateTotal = () => {
            if (totalEl) totalEl.textContent = formatPrice(selectedPrice * qty);
            if (submitBtn) submitBtn.disabled = !selectedVariantId;
        };

        // Applique une variante à l'affichage (prix, prix barré, promo, prix/kg)
        const applyVariant = (v) => {
            selectedVariantId = v.id;
            selectedPrice     = v.price_raw;
            if (priceEl) priceEl.textContent = v.price + ' €';
            if (priceOldEl) {
                priceOldEl.textContent = v.old_price ? v.old_price + ' €' : '';
                priceOldEl.hidden = !v.old_price;
            }
            if (discountEl) {
                discountEl.textContent = v.discount_label || '';
                discountEl.hidden = !v.discount_label;
            }
            if (kiloEl) {
                kiloEl.textContent = v.price_kg || '';
                kiloEl.hidden = !v.price_kg;
            }
            updateTotal();
        };

        const openModal = (product) => {
            // Remplissage produit
            if (thumbEl) { thumbEl.src = product.image; thumbEl.alt = product.name; }
            if (nameEl)  nameEl.textContent  = product.name;
            if (roasterEl) roasterEl.textContent = product.roaster;
            if (grindEl)   grindEl.textContent   = product.grind;
            if (sepEl) sepEl.hidden = !(product.roaster && product.grind);

            // Reset état
            selectedVariantId = null;
            selectedPrice     = 0;
            qty               = 1;
            if (qtyValEl) qtyValEl.textContent = qty;
            if (priceEl)     priceEl.textContent  = '';
            if (priceOldEl)  { priceOldEl.textContent = ''; priceOldEl.hidden = true; }
            if (discountEl)  { discountEl.textContent = ''; discountEl.hidden = true; }
            if (kiloEl)      { kiloEl.textContent = ''; kiloEl.hidden = true; }
            if (chipsEl)  chipsEl.innerHTML    = '<span class="quick-add-modal__loading">…</span>';
            updateTotal();

            // Affichage
            modal.classList.add('is-open');
            modal.removeAttribute('aria-hidden');
            modal.inert = false;
            document.body.style.overflow = 'hidden';

            // Chargement des variantes
            fetch(product.variantsUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
                .then(r => r.json())
                .then(data => {
                    if (!chipsEl) return;
                    chipsEl.innerHTML = '';
                    const variants = (data.success && data.variants) ? data.variants : [];

                    // Libellé de l'axe (Poids / Pièces / Mouture…) fourni par le serveur.
                    if (labelEl && data.axis_label) labelEl.textContent = data.axis_label;

                    // On masque le sélecteur seulement s'il n'y a AUCUNE variante.
                    // Avec une seule variante on affiche quand même le choix, coché,
                    // pour que l'utilisateur voie ce qu'il valide.
                    if (selectorEl) selectorEl.hidden = variants.length === 0;
                    if (!variants.length) return;

                    const single = variants.length === 1;

                    variants.forEach((v, i) => {
                        const chip = document.createElement('button');
                        chip.type      = 'button';
                        chip.className = 'quick-add-modal__chip' + (i === 0 ? ' is-selected' : '') + (single ? ' is-only' : '');
                        chip.textContent = v.label || `Format ${i + 1}`;
                        if (single) chip.setAttribute('aria-disabled', 'true');

                        chip.addEventListener('click', () => {
                            if (single) return;
                            chipsEl.querySelectorAll('.quick-add-modal__chip')
                                   .forEach(c => c.classList.remove('is-selected'));
                            chip.classList.add('is-selected');
                            applyVariant(v);
                        });

                        chipsEl.appendChild(chip);

                        // Sélection auto du premier chip
                        if (i === 0) applyVariant(v);
                    });
                })
                .catch(() => { if (chipsEl) chipsEl.innerHTML = ''; });
        };

        const closeModal = () => {
            modal.classList.remove('is-open');
            modal.setAttribute('aria-hidden', 'true');
            modal.inert = true;
            document.body.style.overflow = '';
        };

        // Fermeture
        modal.querySelectorAll('[data-quick-add-close]').forEach(el => {
            el.addEventListener('click', closeModal);
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
        });

        // Quantité
        if (decBtn) decBtn.addEventListener('click', () => {
            if (qty > 1) { qty--; if (qtyValEl) qtyValEl.textContent = qty; updateTotal(); }
        });
        if (incBtn) incBtn.addEventListener('click', () => {
            qty++; if (qtyValEl) qtyValEl.textContent = qty; updateTotal();
        });

        // Ajout au panier
        if (submitBtn) {
            submitBtn.addEventListener('click', () => {
                if (!selectedVariantId) return;
                document.dispatchEvent(new CustomEvent('bc:cart:add', {
                    detail: { variantId: selectedVariantId, quantity: qty },
                }));
                closeModal();
            });
        }

        // Écoute des clics sur [data-quick-add]
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-quick-add]');
            if (!btn) return;
            if (btn.disabled) return;
            e.preventDefault();
            e.stopImmediatePropagation();

            // Ferme le drawer de recherche sans remettre overflow à ''
            const drawer = document.getElementById('search-drawer');
            if (drawer?.classList.contains('is-open')) {
                drawer.classList.remove('is-open');
                drawer.setAttribute('aria-hidden', 'true');
                drawer.inert = true;
            }

            openModal({
                id:          btn.dataset.productId,
                name:        btn.dataset.productName,
                image:       btn.dataset.productImage,
                roaster:     btn.dataset.productRoaster,
                grind:       btn.dataset.productGrind,
                variantsUrl: btn.dataset.variantsUrl,
            });
        }, true);
    },

    // ─── Swiper favoris du moment (2.5 slides visibles) ──────────────────────
    initFavSwiper() {
        const el = document.querySelector('.search-drawer__fav-swiper');
        if (!el) return;

        this._favSwiper = new Swiper(el, {
            modules: [Navigation],
            slidesPerView: 1.33,
            spaceBetween: 12,
            a11y: { slideRole: '' },
            navigation: {
                prevEl: '.search-drawer__fav-prev',
                nextEl: '.search-drawer__fav-next',
            },
            breakpoints: {
                601: { slidesPerView: 2.5 },
                769: { slidesPerView: 2.2 },
            },
        });
    },

    destroy() {
        this._listeners.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
        this._listeners = [];
        if (this._favSwiper) { this._favSwiper.destroy(true, true); this._favSwiper = null; }
    },
};

export default barcinoSearch;
